import { useState, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  useToast,
  useDisclosure,
  Icon,
  Image,
  Button,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import {
  hoverItemColor,
  primary,
  textPrimary,
  textSecondary,
  white,
} from "../../../../../utils/colors";
import { columns } from "./UserProfileTenantsColumns"; // Adjust the path according to your file structure
import Table from "../../../../../components/shared/Table";
import { useDispatch, useSelector } from "react-redux";
import {
  listUsersAction,
  deleteUserAction,
} from "../../../../../actions/userActions";
import useDebounce from "../../../../../hooks/useDebounce";
import TableContainerWindowed from "../../../../../components/shared/Table/TableContainerWindowed";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../../../../hooks/useIsMobile";
import ConfirmDeleteModal from "../../../../../components/modals/ConfirmDeleteModal";
import { inviteUserToTenantAction } from "../../../../../actions/tenantActions";
import { FaArrowRight } from "react-icons/fa6";
import { TbDental } from "react-icons/tb";

const UserProfileTenants = ({ tenant, slug, userInfo }) => {
  // GENERAL

  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // STATE

  const [tenants, setTenants] = useState(userInfo?.tenants || []);

  const [showButtons, setShowButtons] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userId, setUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [invitePermissions, setInvitePermissions] = useState();

  // LANGUAGE
  const storedLanguage = localStorage.getItem("language");
  const initialLanguage = storedLanguage || "lt"; // Default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(initialLanguage);

  // REDUX
  const dispatch = useDispatch();

  const userList = useSelector((state) => state.userList);
  const { loading, error, success, users, pages } = userList;

  const userDelete = useSelector((state) => state.userDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = userDelete;

  const tenantInviteUserToTenant = useSelector(
    (state) => state.tenantInviteUserToTenant
  );
  const {
    loading: loadingInvite,
    error: errorInvite,
    success: successInvite,
    message,
  } = tenantInviteUserToTenant;

  const tenantUpdateTenantUserPermissions = useSelector(
    (state) => state.tenantUpdateTenantUserPermissions
  );
  const {
    loading: loadingUpdatePermissions,
    error: errorUpdatePermissions,
    success: successUpdatePermissions,
  } = tenantUpdateTenantUserPermissions;

  // PAGINATION
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // FILTERING
  const [resetFilter, setResetFilter] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const { isOpenFilter, onOpenFilter, onCloseFilter } = useDisclosure();

  const filterConfig = {
    employees: true,
  };

  const [filterParams, setFilterParams] = useState({
    status: [],
    startDate: "",
    endDate: "",
    profession: [],
  });

  const deleteHandler = (id) => {
    dispatch(deleteUserAction(slug, id));
  };

  const handleInvite = async () => {
    await dispatch(
      inviteUserToTenantAction(
        slug,
        inviteEmail,
        inviteRole,
        invitePermissions,
        currentLanguage
      )
    );
  };

  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false); // Reset the reset state after applying the filter
    localStorage.setItem("filterSettings_employees", JSON.stringify(filters));

    dispatch(
      listUsersAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder
      )
    );
  };

  const handleResetFilter = () => {
    const resetState = {
      startDate: "",
      endDate: "",
      status: [],
      profession: [],
    };
    setFilterParams(resetState);
    localStorage.setItem(
      "filterSettings_employees",
      JSON.stringify(resetState)
    ); // Reset localStorage

    console.log("Resetting filters", resetState);
    console.log(
      "Resetting filters",
      localStorage.getItem("filterSettings_employees")
    );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      listUsersAction(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder
      )
    );
  };

  //SORTING
  const [sortField, setSortField] = useState("userID");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = useCallback(
    (column) => {
      // Toggle sort order if the same column is clicked, or default to ascending
      const newSortOrder =
        sortField === column && sortOrder === "asc" ? "desc" : "asc";

      // Update sort field and order
      setSortField(column);
      setSortOrder(newSortOrder);

      // setPage(1);
    },
    [sortField, sortOrder]
  );

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1); // Reset page to 1 after search
  };

  // HANDLERS

  // SIDEPANEL HANDLERS
  const openEditSidePanelModal = () => {
    setEditModalOpen(true);
    document.body.classList.add("no-scroll");
  };

  const closeEditSidePanelModal = () => {
    setEditModalOpen(false);
    document.body.classList.remove("no-scroll");
  };

  // USE EFFECT

  // USEEFFECT

  // LOGS
  // console.log("tenant", tenant);
  // console.log("showButtons", showButtons);
  // console.log("clinicName", clinicName);
  // console.log("registrationEmail", registrationEmail);
  // console.log("supportEmail", supportEmail);
  // console.log("phone", phone);
  // console.log("address", address);

  // console.log("tenants", tenants);
  // console.log("message", message);

  // console.log("inviteEmail", inviteEmail);
  // console.log("inviteRole", inviteRole);
  // console.log("invitePermissions", invitePermissions);

  return (
    <>
      {/* SIDEPANELS  */}
      {/* <SidePanel
        open={isOpen}
        close={onClose}
        title={t("user.inviteUser")}
        noBorder={true}
      >
        <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
          <InviteUser
            close={onClose}
            email={inviteEmail}
            setEmail={setInviteEmail}
            role={inviteRole}
            setRole={setInviteRole}
            onInvite={handleInvite}
            loading={loadingInvite}
            permissions={invitePermissions}
            setPermissions={setInvitePermissions}
          />
        </Flex>
      </SidePanel> */}

      {/* <SidePanel
        open={editModalOpen}
        close={closeEditSidePanelModal}
        title={t("user.editUser")}
        noBorder={true}
      >
        <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
          <EditUser
            slug={slug}
            loading={loadingUpdatePermissions}
            close={closeEditSidePanelModal}
            user={selectedUser}
          />
        </Flex>
      </SidePanel> */}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          deleteTitle={t("service.deleteServiceTitle")}
          // deleteName={serviceName}
          warningLabel={t("service.deleteWarning")}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            deleteHandler(userId);
            setShowDeleteConfirmation(false);
          }}
        />
      )}

      {/* MODALS END */}

      <Box
        w="100%"
        p={["3rem 0rem 0rem", "3rem 0rem 3rem"]}
        borderBottom={[
          `0px solid ${newBorderMode}`,
          `1px solid ${newBorderMode}`,
        ]}
      >
        <Flex
          w="100%"
          justify="space-between"
          gap="2rem"
          direction={["column", "row"]}
        >
          {/* LEFT START */}
          <Flex
            direction="column"
            gap={2}
            w={["100%", "40%"]}
            p={["0 1.5rem", "initial"]}
          >
            <Heading fontSize="1rem" fontWeight="600">
              {t("user.userTenants")}
            </Heading>
            <Text
              color={textSecondary}
              fontSize="sm"
              fontWeight="500"
              maxW="90%"
            >
              {t("user.userTenantsDescription")}
            </Text>
          </Flex>
          {/* LEFT END */}

          {/* RIGHT START */}
          <Flex
            w={["100%", "800px"]}
            direction="column"
            justify="flex-start"
            align="flex-start"
          >
            <Flex
              direction="column"
              w="100%"
              bg={backgroundLightMode}
              borderRadius="1rem"
              p="1.25rem 1.5rem 1.25rem"
            >
              {!isMobile ? (
                <>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={whiteMode}
                    borderRadius="1rem"
                    p="1rem 0rem 0rem"
                  >
                    <Box p="0 1.5rem 1rem">
                      <Flex
                        w="100%"
                        align="center"
                        justify="space-between"
                        p="0.75rem 0"
                      >
                        <Heading
                          fontSize="md"
                          fontWeight="600"
                          color={textPrimaryMode}
                          lineHeight="1"
                        >
                          {t("user.tenants")}
                        </Heading>
                      </Flex>
                    </Box>

                    {!loading ? (
                      <>
                        <TableContainerWindowed
                          columns={columns(
                            t,
                            navigate,
                            isMobile,
                            textPrimaryMode,
                            whiteMode,
                            t,
                            handleSortChange,
                            deleteHandler,
                            setUserId,
                            setShowDeleteConfirmation
                          )}
                          data={tenants}
                          loading={loading}
                          error={error}
                          onRowClick={(userId) => {
                            const user = users.find((u) => u._id === userId);
                            if (user) {
                              setSelectedUser(user);
                              openEditSidePanelModal();
                            }
                          }}
                          noItemsTitle={t("noItems.noEmployees")}
                          noItemsDescription={t("noItems.noEmployeesText")}
                          noItemsButtonLabel={t("noItems.noEmployeesButton")}
                          // noItemsButtonHandler={openCreateEmployeeSidePanelModal}
                          sortField={sortField}
                          sortOrder={sortOrder}
                          handleSortChange={handleSortChange}
                        />
                        <Flex p="0rem 0 0rem" w="100%">
                          <Table.Pagination
                            isSmall
                            loading={loading}
                            page={page}
                            pages={pages}
                            pageSize={pageSize}
                            pageSizes={pageSizes}
                            navigate={navigate}
                            setPage={setPage}
                            setPageSize={setPageSize}
                            totalCount={users && users.length}
                          />
                        </Flex>
                      </>
                    ) : (
                      <Flex
                        w="100%"
                        justify="center"
                        align="flex-start"
                        p="3rem 2rem"
                        minH="800px"
                      >
                        <Spinner size="lg" color={primary} />
                      </Flex>
                    )}
                  </Flex>
                </>
              ) : (
                <>
                  {!loading ? (
                    <>
                      <Box>
                        <Button
                          size="lg"
                          h="4.5rem"
                          borderRadius="1.5rem"
                          bg={white}
                          border={`1px solid ${newBorderMode}`}
                          key={tenant.tenantID}
                          w="100%"
                          px="1.25rem"
                          // onClick={() => handleTenantSwitch(tenant)}
                          _hover={{
                            bg: hoverItemColor,
                            border: `1px solid ${hoverItemColor}`,
                          }}
                          role="group"
                        >
                          <Flex w="100%" justify="space-between" align="center">
                            <Flex align="cemter">
                              {tenant && tenant.logo ? (
                                <Image
                                  src={tenant.logo}
                                  h="2.5rem"
                                  w="2.5rem"
                                  borderRadius="100%"
                                  objectFit="cover"
                                  mr="0.5rem"
                                />
                              ) : (
                                <Flex
                                  align="center"
                                  justify="center"
                                  h="2.5rem"
                                  w="2.5rem"
                                  borderRadius="100%"
                                  bg={backgroundLightMode}
                                  // border={`1px solid ${newBorder}`}
                                  mr="0.5rem"
                                >
                                  <Icon
                                    as={TbDental}
                                    color={textSecondary}
                                    fontSize="0.875rem"
                                  />
                                </Flex>
                              )}

                              <Flex
                                align="flex-start"
                                justify="flex-start"
                                gap="3px"
                                direction="column"
                              >
                                <Text
                                  fontSize="1rem"
                                  color={textPrimary}
                                  textTransform="capitalize"
                                >
                                  {tenant.clinicName}
                                </Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="500"
                                  color="gray.500"
                                >
                                  {t(`role.${tenant.role}`)}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex align="center" justify="flex-end">
                              <Flex
                                align="center"
                                justify="center"
                                position="relative"
                                h="1.5rem"
                                w="1.5rem"
                                overflow="hidden"
                                // bg={backgroundLight}
                              >
                                <Icon
                                  position="absolute"
                                  as={FaArrowRight}
                                  color={textSecondary}
                                  fontSize="0.875rem"
                                  _groupHover={{
                                    transform: "translateX(60px)",
                                    transition: "transform 0.4s ease",
                                    color: textPrimary,
                                  }}
                                />
                                <Icon
                                  position="absolute"
                                  transform="translateX(-60px)"
                                  as={FaArrowRight}
                                  color={textSecondary}
                                  fontSize="0.875rem"
                                  _groupHover={{
                                    transform: "translateX(0px)",
                                    transition: "transform 0.4s ease",
                                    color: textPrimary,
                                  }}
                                />
                              </Flex>
                            </Flex>
                          </Flex>
                        </Button>
                      </Box>
                      <Flex p="0rem 0 0rem" w="100%">
                        <Table.Pagination
                          isSmall
                          loading={loading}
                          page={page}
                          pages={pages}
                          pageSize={pageSize}
                          pageSizes={pageSizes}
                          navigate={navigate}
                          setPage={setPage}
                          setPageSize={setPageSize}
                          totalCount={users && users.length}
                        />
                      </Flex>
                    </>
                  ) : (
                    <Flex
                      w="100%"
                      justify="center"
                      align="flex-start"
                      p="3rem 2rem"
                      minH="800px"
                    >
                      <Spinner size="lg" color={primary} />
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {/* RIGHT END */}
        </Flex>

        {/* BUTTONS */}
      </Box>
    </>
  );
};

export default UserProfileTenants;
